import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { replayApi, type ReplayListItem } from '../../services/replayApi';
import './ReplaysPage.css';

const ReplaysPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [replays, setReplays] = useState<ReplayListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadReplays();
    }
  }, [user]);

  const loadReplays = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await replayApi.getPlayerReplays(user.id, 20);
      setReplays(data);
    } catch (err) {
      console.error('Failed to load replays:', err);
      setError(err instanceof Error ? err.message : 'Failed to load replays');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="replays-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading replays...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="replays-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadReplays} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="replays-page">
      <div className="replays-header">
        <h1>🎬 Game Replays</h1>
        <p className="header-subtitle">Watch your past games</p>
      </div>

      {replays.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎬</div>
          <h2>No Replays Available</h2>
          <p>Play some games to see replays here!</p>
        </div>
      ) : (
        <div className="replays-grid">
          {replays.map((replay) => (
            <div 
              key={replay._id} 
              className="replay-card"
              onClick={() => navigate(`/replays/${replay.gameId}`)}
            >
              <div className="replay-card-header">
                <div className="game-type-badge">🎴 {replay.gameType}</div>
                <div className="snapshot-count">{replay.metadata.snapshotCount} snapshots</div>
              </div>
              
              <div className="replay-card-body">
                <div className="players-list">
                  <div className="players-label">Players:</div>
                  {replay.playerNames.map((name, idx) => (
                    <div key={idx} className="player-name-chip">
                      {name}
                    </div>
                  ))}
                </div>
                
                <div className="winner-info">
                  <span className="winner-label">Winner:</span>
                  <span className="winner-name">🏆 {replay.finalResult.winner}</span>
                </div>
              </div>
              
              <div className="replay-card-footer">
                <div className="replay-date">
                  {replayApi.formatTimestamp(replay.completedAt)}
                </div>
                <div className="replay-duration">
                  ⏱️ {replayApi.formatDuration(replay.duration)}
                </div>
              </div>
              
              <div className="watch-overlay">
                <button className="watch-btn">▶️ Watch Replay</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReplaysPage;
